/**
 * Signal Enhanced Component
 * 
 * Unique Properties:
 * - Event routing and correlation management
 * - Signal payload builder with JSON editor
 * - Target instance selector with filtering
 * - Broadcast capabilities to multiple instances
 * - Signal history and delivery tracking
 * - Retry mechanisms and error handling
 * - Event bus visualization
 */

import {
    Alert,
    Badge,
    Button,
    Card,
    Checkbox,
    Code,
    Container,
    Grid,
    Group,
    JsonInput,
    Modal,
    MultiSelect,
    NumberInput,
    Paper,
    Progress,
    Radio,
    SegmentedControl,
    Select,
    Stack,
    Tabs,
    Text,
    TextInput,
    ThemeIcon,
    Title
} from '@mantine/core';
import '@mantine/core/styles.css';
import { notifications } from '@mantine/notifications';
import {
    IconBolt,
    IconBroadcast,
    IconCheck,
    IconClock,
    IconHistory,
    IconMail,
    IconNetwork,
    IconRefresh,
    IconRoute,
    IconSend,
    IconTarget
} from '@tabler/icons-react';
import React, { useEffect, useRef, useState } from 'react';
import { TabulatorFull } from 'tabulator-tables';
import 'tabulator-tables/dist/css/tabulator_midnight.min.css';
import { useSignalReceived } from '../hooks/useSubscriptions';
import { useInstances, useSignalMutations, useSignals } from '../hooks/useWorkflowState';

interface Signal {
    id: string;
    name: string;
    event_type: string;
    payload: Record<string, unknown>;
    correlation_id?: string;
    target_instances?: string[];
    broadcast?: boolean;
    priority: number;
    retry_count?: number;
    timeout_seconds?: number;
    delivery_mode: 'immediate' | 'queued' | 'scheduled';
}

interface SignalDelivery {
    signal_id: string;
    instance_id: string;
    timestamp: string;
    status: 'pending' | 'delivered' | 'failed' | 'retrying';
    attempts: number;
    error?: string | null;
}

interface SignalProps {
    id?: string;
    data?: Signal;
    mode?: 'send' | 'view' | 'test';
    onSend?: (signal: Signal) => void;
}

export const SignalEnhanced: React.FC<SignalProps> = ({
    id,
    data = {} as Signal,
    mode = 'send',
    onSend
}) => {
    const [activeTab, setActiveTab] = useState('compose');
    const [signalName, setSignalName] = useState(data.name || '');
    const [eventType, setEventType] = useState(data?.event_type || '');
    const [payload, setPayload] = useState(data.payload || {});
    const [targetMode, setTargetMode] = useState<'specific' | 'broadcast' | 'filter'>('specific');
    const [selectedInstances, setSelectedInstances] = useState<string[]>(data.target_instances || []);
    const [correlationId, setCorrelationId] = useState(data.correlation_id || '');
    const [priority, setPriority] = useState(data.priority || 50);
    const [deliveryMode, setDeliveryMode] = useState(data.delivery_mode || 'immediate');
    const [retryCount, setRetryCount] = useState(data.retry_count || 3);
    const [modalOpened, setModalOpened] = useState(false);
    const [selectedDelivery, setSelectedDelivery] = useState<SignalDelivery | null>(null);

    const deliveryTableRef = useRef<HTMLDivElement>(null);
    const deliveryTabulator = useRef<InstanceType<typeof TabulatorFull> | null>(null);
    const historyTableRef = useRef<HTMLDivElement>(null);
    const historyTabulator = useRef<InstanceType<typeof TabulatorFull> | null>(null);

    // Use real GraphQL data instead of mock data
    const { items: signals, loading: signalsLoading, error: signalsError, refetch: refetchSignals } = useSignals();
    const { items: instances, loading: instancesLoading } = useInstances();
    const targetInstance = selectedInstances[0] || '';
    const { sendSignal, updateSignal, deleteSignal, loading: mutationLoading } = useSignalMutations();

    // Real-time subscription for signal events
    const signalEvents = useSignalReceived(
        (data?.target_instances?.[0] || ''),
        'test',
        {
            onData: (data) => {
                console.log('Signal received:', data);
                notifications.show({
                    title: 'Signal Received',
                    message: `Signal "${data.signalName}" received on instance ${data?.instanceId}`,
                    color: 'blue'
                });

                // Update delivery history with the received signal
                if (deliveryTabulator.current) {
                    const existingRow = deliveryTabulator.current.searchData('instance_id', '=', String(data?.instanceId));
                    if (existingRow.length > 0) {
                        existingRow[0].update({
                            status: (data.result === 'SUCCESS' ? 'delivered' : 'failed') as 'delivered' | 'failed',
                            attempts: (existingRow[0].getData().attempts || 0) + 1,
                            last_attempt: data.timestamp
                        });
                    }
                }
            }
        }
    );

    // Transform instances for select options
    const availableInstances = Array.isArray(instances) ? instances.map(instance => ({
        value: instance.id,
        label: `${instance.name} (#${instance.id.slice(-4)})`
    })) : [];

    // Signal templates from data or predefined
    const signalTemplates = [
        { name: 'approve', payload: { approved: true, approver: 'user_id' } },
        { name: 'reject', payload: { reason: 'Invalid data' } },
        { name: 'retry', payload: { attempt: 1, max_attempts: 3 } },
        { name: 'escalate', payload: { level: 2, assignee: 'manager_id' } },
        { name: 'timeout', payload: { expired_at: new Date().toISOString() } }
    ];

    // Transform signals for delivery history
    type RawSignal = {
        id?: unknown;
        instanceId?: unknown;
        sentAt?: unknown;
        status?: unknown;
        retryCount?: unknown;
        error?: unknown;
    };
    const toSignalDelivery = (row: unknown): SignalDelivery | null => {
        const s = row as RawSignal;
        if (typeof s !== 'object' || s === null) return null;
        const id = typeof s.id === 'string' ? s.id : '';
        const instanceId = typeof s.instanceId === 'string' ? s.instanceId : '';
        const timestamp = typeof s.sentAt === 'string' || typeof s.sentAt === 'number'
            ? String(s.sentAt)
            : new Date().toISOString();
        const statusStr = typeof s.status === 'string' ? s.status.toLowerCase() : '';
        const status: SignalDelivery['status'] =
            statusStr === 'pending' ? 'pending'
            : statusStr === 'delivered' ? 'delivered'
            : statusStr === 'failed' ? 'failed'
            : statusStr === 'retrying' ? 'retrying'
            : 'delivered';
        const attempts = typeof s.retryCount === 'number' ? s.retryCount : Number(s.retryCount || 1);
        const error = typeof s.error === 'string' ? s.error : null;
        return { signal_id: id, instance_id: instanceId, timestamp, status, attempts, error };
    };
    const deliveryHistory: SignalDelivery[] = Array.isArray(signals)
        ? (signals.map((row) => toSignalDelivery(row)).filter((x): x is SignalDelivery => x !== null))
        : [];

    // Initialize delivery tracking table
    useEffect(() => {
        if (deliveryTableRef.current && !deliveryTabulator.current) {
            deliveryTabulator.current = new TabulatorFull(deliveryTableRef.current, {
                data: selectedInstances.map(instanceId => ({
                    instance_id: String(instanceId),
                    status: 'pending',
                    attempts: 0,
                    last_attempt: null
                })),
                layout: 'fitColumns',
                responsiveLayout: 'hide',
                columns: [
                    {
                        title: 'Instance',
                        field: 'instance_id',
                        formatter: (cell: { getValue: () => string }) => {
                            const value = cell.getValue();
                            return `<code>${value}</code>`;
                        }
                    },
                    {
                        title: 'Status',
                        field: 'status',
                        formatter: (cell: { getValue: () => string }) => {
                            const value = cell.getValue();
                            const colors = {
                                pending: 'orange',
                                delivered: 'green',
                                failed: 'red',
                                retrying: 'blue'
                            } as const;
                            const icons = {
                                pending: '⏳',
                                delivered: '✅',
                                failed: '❌',
                                retrying: '🔄'
                            } as const;
                            const color = (colors as Record<string, string>)[value] || 'gray';
                            const icon = (icons as Record<string, string>)[value] || '❓';
                            return `<span style="color: ${color};">${icon} ${value}</span>`;
                        }
                    },
                    {
                        title: 'Attempts',
                        field: 'attempts'
                    },
                    {
                        title: 'Last Attempt',
                        field: 'last_attempt',
                        formatter: (cell: { getValue: () => string | null }) => {
                            const value = cell.getValue();
                            return value || 'Not attempted';
                        }
                    }
                ]
            });
        }
    }, [selectedInstances]);

    // Initialize history table
    useEffect(() => {
        if (historyTableRef.current && !historyTabulator.current) {
            historyTabulator.current = new TabulatorFull(historyTableRef.current, {
                data: deliveryHistory,
                layout: 'fitColumns',
                responsiveLayout: 'hide',
                pagination: 'local',
                paginationSize: 10,
                columns: [
                    {
                        title: 'Signal ID',
                        field: 'signal_id',
                        headerFilter: true
                    },
                    {
                        title: 'Instance',
                        field: 'instance_id'
                    },
                    {
                        title: 'Timestamp',
                        field: 'timestamp'
                    },
                    {
                        title: 'Status',
                        field: 'status',
                        formatter: (cell: { getValue: () => string }) => {
                            const value = cell.getValue();
                            const config = {
                                delivered: { color: 'green', icon: '✅' },
                                failed: { color: 'red', icon: '❌' },
                                retrying: { color: 'orange', icon: '🔄' },
                                pending: { color: 'blue', icon: '⏳' }
                            } as const;
                            const cfg = (config as Record<string, { color: string; icon: string }>)[value] || { color: 'gray', icon: '❓' };
                            const { color, icon } = cfg;
                            return `<span style="color: ${color};">${icon} ${value}</span>`;
                        }
                    },
                    {
                        title: 'Error',
                        field: 'error',
                        formatter: (cell: { getValue: () => string | null }) => {
                            const value = cell.getValue();
                            return value ? `<span style="color: red;">${value}</span>` : '-';
                        }
                    }
                ]
            });
        }
    }, []);

    // Event bus visualization
    const renderEventBus = () => {
        return (
            <Card withBorder>
                <Title order={5} mb="md">Event Bus Flow</Title>
                <div style={{ position: 'relative', height: 300, background: '#f8f9fa', borderRadius: 8, padding: 20 }}>
                    {/* Signal Source */}
                    <div style={{
                        position: 'absolute',
                        left: 50,
                        top: 130,
                        width: 100,
                        height: 40,
                        background: '#339af0',
                        color: 'white',
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold'
                    }}>
                        📡 Signal
                    </div>

                    {/* Event Bus */}
                    <div style={{
                        position: 'absolute',
                        left: 200,
                        top: 100,
                        width: 200,
                        height: 100,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: 12,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: 18,
                        fontWeight: 'bold'
                    }}>
                        🚌 Event Bus
                    </div>

                    {/* Target Instances */}
                    {selectedInstances.slice(0, 3).map((instanceId, idx) => (
                        <div
                            key={instanceId}
                            style={{
                                position: 'absolute',
                                right: 50,
                                top: 50 + idx * 70,
                                width: 120,
                                height: 40,
                                background: '#51cf66',
                                color: 'white',
                                borderRadius: 8,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 12
                            }}
                        >
                            🎯 {instanceId}
                        </div>
                    ))}

                    {/* Arrows */}
                    <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
                        <defs>
                            <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                                <polygon points="0 0, 10 3.5, 0 7" fill="#339af0" />
                            </marker>
                        </defs>
                        {/* Signal to Event Bus */}
                        <line x1="150" y1="150" x2="200" y2="150" stroke="#339af0" strokeWidth="2" markerEnd="url(#arrowhead)" />
                        {/* Event Bus to Instances */}
                        {selectedInstances.slice(0, 3).map((_: any, idx) => (
                            <line
                                key={idx}
                                x1="400"
                                y1="150"
                                x2="450"
                                y2={70 + idx * 70}
                                stroke="#51cf66"
                                strokeWidth="2"
                                markerEnd="url(#arrowhead)"
                            />
                        ))}
                    </svg>
                </div>
            </Card>
        );
    };

    const handleSendSignal = async () => {
        if (!signalName || !targetInstance) {
            notifications.show({
                title: 'Validation Error',
                message: 'Signal name and target instance are required',
                color: 'red'
            });
            return;
        }

        try {
            const signalData = {
                instanceId: targetInstance,
                signalName,
                payload,
                metadata: correlationId ? {
                    correlation: correlationId,
                    source: 'signal-enhanced',
                    timestamp: new Date().toISOString(),
                    priority: 'normal' as const
                } : undefined
            };

            await sendSignal(signalName, signalData);

            notifications.show({
                title: 'Signal Sent',
                message: `Signal "${signalName}" sent successfully`,
                color: 'green'
            });

            // Refresh signals list
            await refetchSignals();

            // Reset form
            setSignalName('');
            setPayload({});
            setCorrelationId('');
        } catch (error) {
            console.error('Send signal failed:', error);
            notifications.show({
                title: 'Send Failed',
                message: error instanceof Error ? error.message : 'Failed to send signal',
                color: 'red'
            });
        }
    };

    // Handle loading states
    if (signalsLoading || instancesLoading) {
        return (
            <Container size="xl">
                <Paper shadow="sm" p="lg">
                    <Stack ta="center" gap="md">
                        <Progress value={40} animated size="lg" style={{ width: '100%' }} />
                        <Text>Loading signal data...</Text>
                    </Stack>
                </Paper>
            </Container>
        );
    }

    // Handle error states
    if (signalsError) {
        return (
            <Container size="xl">
                <Paper shadow="sm" p="lg">
                    <Alert color="red" title="Error Loading Data">
                        <Text>{signalsError?.message}</Text>
                        <Button
                            mt="md"
                            variant="outline"
                            onClick={() => refetchSignals()}
                            leftSection={<IconRefresh size={16} />}
                        >
                            Retry
                        </Button>
                    </Alert>
                </Paper>
            </Container>
        );
    }

    return (
        <Container size="xl">
            <Paper shadow="sm" p="lg">
                <Group justify="space-between" mb="lg">
                    <div>
                        <Title order={2}>Signal Management</Title>
                        <Text color="dimmed">Send events and signals to workflow instances</Text>
                    </div>
                    <Group>
                        <Badge size="lg" leftSection={<IconBolt size={14} />}>
                            {mode === 'send' ? 'Send Mode' : mode === 'test' ? 'Test Mode' : 'View Mode'}
                        </Badge>
                        {mode === 'send' && (
                            <Button
                                leftSection={<IconSend size={16} />}
                                onClick={handleSendSignal}
                                loading={mutationLoading}
                                disabled={!signalName || !targetInstance}
                            >
                                Send Signal
                            </Button>
                        )}
                    </Group>
                </Group>

                <Tabs value={activeTab} onChange={(v) => setActiveTab(v ?? '')}>
                    <Tabs.List>
                        <Tabs.Tab value="compose" leftSection={<IconMail size={16} />}>
                            Compose
                        </Tabs.Tab>
                        <Tabs.Tab value="targets" leftSection={<IconTarget size={16} />}>
                            Targets
                        </Tabs.Tab>
                        <Tabs.Tab value="routing" leftSection={<IconRoute size={16} />}>
                            Routing
                        </Tabs.Tab>
                        <Tabs.Tab value="delivery" leftSection={<IconNetwork size={16} />}>
                            Delivery
                        </Tabs.Tab>
                        <Tabs.Tab value="history" leftSection={<IconHistory size={16} />}>
                            History
                        </Tabs.Tab>
                        <Tabs.Tab value="visualization" leftSection={<IconBroadcast size={16} />}>
                            Event Bus
                        </Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value="compose">
                        <Grid mt="md">
                            <Grid.Col span={8}>
                                <Stack>
                                    <TextInput
                                        label="Signal Name"
                                        placeholder="Enter signal name (e.g., approve, reject, timeout)"
                                        value={signalName}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSignalName(e.currentTarget.value)}
                                        required
                                    />

                                    <TextInput
                                        label="Event Type"
                                        placeholder="Enter event type"
                                        value={eventType}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEventType(e.currentTarget.value)}
                                        required
                                    />

                                    <JsonInput
                                        label="Payload"
                                        placeholder="Enter JSON payload"
                                        minRows={10}
                                        value={JSON.stringify(payload, null, 2)}
                                        onChange={(value) => setPayload(JSON.parse(value))}
                                        formatOnBlur
                                    />

                                    <TextInput
                                        label="Correlation ID (Optional)"
                                        placeholder="Enter correlation ID for tracking"
                                        value={correlationId}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCorrelationId(e.currentTarget.value)}
                                    />
                                </Stack>
                            </Grid.Col>

                            <Grid.Col span={4}>
                                <Stack>
                                    <Card withBorder>
                                        <Title order={6} mb="md">Signal Templates</Title>
                                        <Stack gap="xs">
                                            {signalTemplates.map((template) => (
                                                <Button
                                                    key={template.name}
                                                    variant="light"
                                                    size="xs"
                                                    fullWidth
                                                    onClick={() => {
                                                        setSignalName(template.name);
                                                        setPayload(template.payload);
                                                    }}
                                                >
                                                    {template.name}
                                                </Button>
                                            ))}
                                        </Stack>
                                    </Card>

                                    <Card withBorder>
                                        <Title order={6} mb="md">Delivery Options</Title>
                                        <Stack gap="xs">
                                            <NumberInput
                                                label="Priority"
                                                min={1}
                                                max={100}
                                                value={priority}
                                                onChange={(value: number | string | null) => setPriority((typeof value === 'number' ? value : null) || 50)}
                                            />

                                            <Select
                                                label="Delivery Mode"
                                                data={[
                                                    { value: 'immediate', label: 'Immediate' },
                                                    { value: 'queued', label: 'Queued' },
                                                    { value: 'scheduled', label: 'Scheduled' }
                                                ]}
                                                value={deliveryMode}
                                                onChange={(value: string | null) => setDeliveryMode((value ?? 'immediate') as typeof deliveryMode)}
                                            />

                                            <NumberInput
                                                label="Retry Count"
                                                min={0}
                                                max={10}
                                                value={retryCount}
                                                onChange={(value: number | string | null) => setRetryCount((typeof value === 'number' ? value : null) || 0)}
                                            />
                                        </Stack>
                                    </Card>
                                </Stack>
                            </Grid.Col>
                        </Grid>
                    </Tabs.Panel>

                    <Tabs.Panel value="targets">
                        <Stack mt="md">
                            <SegmentedControl
                                value={targetMode}
                                onChange={(value) => setTargetMode(value as typeof targetMode)}
                                data={[
                                    { label: 'Specific Instances', value: 'specific' },
                                    { label: 'Broadcast', value: 'broadcast' },
                                    { label: 'Filter', value: 'filter' }
                                ]}
                            />

                            {targetMode === 'specific' && (
                                <MultiSelect
                                    label="Select Target Instances"
                                    placeholder="Choose instances to receive the signal"
                                    data={availableInstances}
                                    value={selectedInstances}
                                    onChange={setSelectedInstances}
                                    searchable
                                />
                            )}

                            {targetMode === 'broadcast' && (
                                <Alert icon={<IconBroadcast size={16} />} color="blue">
                                    Signal will be broadcast to all active instances
                                </Alert>
                            )}

                            {targetMode === 'filter' && (
                                <Stack>
                                    <Select
                                        label="Filter Field"
                                        data={['status', 'state', 'workflow_id', 'tenant']}
                                    />
                                    <Select
                                        label="Operator"
                                        data={['equals', 'contains', 'starts_with', 'ends_with']}
                                    />
                                    <TextInput
                                        label="Value"
                                        placeholder="Enter filter value"
                                    />
                                </Stack>
                            )}

                            <Card withBorder>
                                <Text fw={500} mb="xs">Selected Targets: {selectedInstances.length}</Text>
                                {selectedInstances.map(id => (
                                    <Badge key={id} variant="light" mr="xs" mb="xs">
                                        {id}
                                    </Badge>
                                ))}
                            </Card>
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="routing">
                        <Stack mt="md">
                            <Alert icon={<IconRoute size={16} />} color="orange">
                                Configure how signals are routed to target instances
                            </Alert>

                            <Grid>
                                <Grid.Col span={6}>
                                    <Card withBorder>
                                        <Title order={6} mb="md">Routing Rules</Title>
                                        <Stack gap="xs">
                                            <Checkbox label="Use correlation for routing" defaultChecked />
                                            <Checkbox label="Route to parent instances" />
                                            <Checkbox label="Route to child instances" />
                                            <Checkbox label="Use topic-based routing" />
                                        </Stack>
                                    </Card>
                                </Grid.Col>

                                <Grid.Col span={6}>
                                    <Card withBorder>
                                        <Title order={6} mb="md">Routing Strategy</Title>
                                        <Radio.Group defaultValue="all">
                                            <Stack gap="xs">
                                                <Radio value="all" label="Send to all matches" />
                                                <Radio value="first" label="Send to first match only" />
                                                <Radio value="random" label="Send to random match" />
                                                <Radio value="round_robin" label="Round robin distribution" />
                                            </Stack>
                                        </Radio.Group>
                                    </Card>
                                </Grid.Col>
                            </Grid>
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="delivery">
                        <Stack mt="md">
                            <Alert icon={<IconNetwork size={16} />} color="green">
                                Real-time delivery tracking for sent signals
                            </Alert>

                            <div ref={deliveryTableRef} />

                            <Card withBorder>
                                <Title order={6} mb="md">Delivery Statistics</Title>
                                <Grid>
                                    <Grid.Col span={3}>
                                        <Text size="sm" color="dimmed">Success Rate</Text>
                                        <Progress value={75} color="green" mt="xs" />
                                    </Grid.Col>
                                    <Grid.Col span={3}>
                                        <Text size="sm" color="dimmed">Avg Delivery Time</Text>
                                        <Text fw={500}>23ms</Text>
                                    </Grid.Col>
                                    <Grid.Col span={3}>
                                        <Text size="sm" color="dimmed">Retry Rate</Text>
                                        <Text fw={500}>12%</Text>
                                    </Grid.Col>
                                    <Grid.Col span={3}>
                                        <Text size="sm" color="dimmed">Failed</Text>
                                        <Text fw={500} color="red">2</Text>
                                    </Grid.Col>
                                </Grid>
                            </Card>
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="history">
                        <Stack mt="md">
                            <Group justify="space-between">
                                <Text fw={500}>Signal History</Text>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    leftSection={<IconRefresh size={16} />}
                                >
                                    Refresh
                                </Button>
                            </Group>

                            <div ref={historyTableRef} />
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="visualization">
                        <Stack mt="md">
                            {renderEventBus()}

                            <Grid>
                                <Grid.Col span={4}>
                                    <Card withBorder>
                                        <Group justify="space-between" mb="xs">
                                            <Text fw={500}>Signals Sent</Text>
                                            <ThemeIcon color="blue" variant="light">
                                                <IconSend size={16} />
                                            </ThemeIcon>
                                        </Group>
                                        <Title order={3}>1,234</Title>
                                        <Text size="xs" color="dimmed">Last hour</Text>
                                    </Card>
                                </Grid.Col>

                                <Grid.Col span={4}>
                                    <Card withBorder>
                                        <Group justify="space-between" mb="xs">
                                            <Text fw={500}>Active Routes</Text>
                                            <ThemeIcon color="green" variant="light">
                                                <IconRoute size={16} />
                                            </ThemeIcon>
                                        </Group>
                                        <Title order={3}>42</Title>
                                        <Text size="xs" color="dimmed">Currently active</Text>
                                    </Card>
                                </Grid.Col>

                                <Grid.Col span={4}>
                                    <Card withBorder>
                                        <Group justify="space-between" mb="xs">
                                            <Text fw={500}>Queue Size</Text>
                                            <ThemeIcon color="orange" variant="light">
                                                <IconClock size={16} />
                                            </ThemeIcon>
                                        </Group>
                                        <Title order={3}>89</Title>
                                        <Text size="xs" color="dimmed">Pending delivery</Text>
                                    </Card>
                                </Grid.Col>
                            </Grid>
                        </Stack>
                    </Tabs.Panel>
                </Tabs>
            </Paper>

            {/* Success Modal */}
            <Modal
                opened={modalOpened}
                onClose={() => setModalOpened(false)}
                title="Signal Sent"
                size="md"
            >
                <Stack>
                    <Alert icon={<IconCheck size={16} />} color="green">
                        Signal has been successfully queued for delivery
                    </Alert>

                    <Card withBorder>
                        <Text size="sm" color="dimmed">Signal ID</Text>
                        <Code>{`temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`}</Code>
                    </Card>

                    <Card withBorder>
                        <Text size="sm" color="dimmed">Targets</Text>
                        <Text>{selectedInstances.length} instances</Text>
                    </Card>

                    <Card withBorder>
                        <Text size="sm" color="dimmed">Expected Delivery</Text>
                        <Text>Within 100ms</Text>
                    </Card>

                    <Button onClick={() => setModalOpened(false)}>
                        Close
                    </Button>
                </Stack>
            </Modal>
        </Container>
    );
};

export default SignalEnhanced;
